import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

/**
 * Container — page-level horizontal layout primitive.
 *
 * Layout model (viewport-wide, content centered with mx-auto):
 *
 *   ┌─ viewport ─────────────────────────────────────────┐
 *   │  padding │       content (max-w)        │ padding  │
 *   │   ──→    │                              │   ←──    │
 *   └────────────────────────────────────────────────────┘
 *
 *
 * 1) Page gutters (px-*) follow the design system token
 *    `--spacing-grid-section-horizontal-margin-page-base`:
 *
 *      breakpoint    px        token value
 *      ──────────    ────      ───────────
 *      base          16px      1rem
 *      md  (≥640)    24px      1.5rem
 *      lg  (≥1024)   48px      3rem
 *      xl  (≥1280)   64px      4rem
 *      2xl (≥1536)   80px      5rem   ← reference breakpoint
 *
 *    Set `padding="none"` to drop the gutters entirely — useful when
 *    Container is nested inside another Container that already
 *    provides the page gutters (e.g. inner content sizing in a Hero).
 *    With `padding="none"`, max-w equals the raw `size` value (no
 *    10rem baked in, see point 2).
 *
 *
 * 2) `size` is the desired *content* width (the inner box, not the
 *    border-box). Because padding lives on the same element, max-w
 *    has to bake in the gutter so the content area lands on `size`.
 *    We calibrate at 2xl, where each gutter is 5rem (10rem / 160px total):
 *
 *      max-w = size + 10rem (160px)         (padding="default")
 *      max-w = size                         (padding="none")
 *
 *                  ╔═══ content ═════╗
 *      size        ║   (what you    ║      max-w (default)   max-w (none)
 *                  ║    actually    ║
 *                  ║      see)      ║
 *      ──────────  ║                ║      ─────────────────  ──────────────
 *      extralarge  ║ 90rem  (1440px)║   →  100rem  (1600px)   90rem  (1440px)
 *      large       ║ 79rem  (1264px)║   →   89rem  (1424px)   79rem  (1264px)
 *      medium      ║ 63rem  (1008px)║   →   73rem  (1168px)   63rem  (1008px)
 *      small       ║ 40.5rem (648px)║   →   50.5rem (808px)   40.5rem (648px)
 *      full        ║   —            ║   →   none              none
 *                  ╚════════════════╝
 *
 *    Below 2xl the gutter shrinks faster than max-w, so content can
 *    overshoot `size` by a few rem at xl/lg. That's intentional — at
 *    smaller viewports the content fills naturally and the cap only
 *    matters at 2xl+.
 */
const sizeStyles = cva("mx-auto w-full", {
	variants: {
		size: {
			extralarge: "max-w-[126rem]", // 90rem + 10rem  (1440px + 160px = 1600px)
			large: "max-w-[115rem]", //       79rem + 10rem  (1264px + 160px = 1424px)
			medium: "max-w-[73rem]", //      63rem + 10rem  (1008px + 160px = 1168px)
			small: "max-w-[50.5rem]", //    40.5rem + 10rem ( 648px + 160px =  808px)
			full: "max-w-none",
		},
		padding: {
			default: "px-4 md:px-6 lg:px-12 xl:px-16 2xl:px-20",
			none: "",
		},
	},
	compoundVariants: [
		// padding="none": drop the baked-in 10rem so content = `size`
		{ size: "extralarge", padding: "none", class: "max-w-[90rem]" },
		{ size: "large", padding: "none", class: "max-w-[79rem]" },
		{ size: "medium", padding: "none", class: "max-w-[63rem]" },
		{ size: "small", padding: "none", class: "max-w-[40.5rem]" },
	],
	defaultVariants: {
		size: "extralarge",
		padding: "default",
	},
});

type ContainerProps = PropsWithChildren &
	VariantProps<typeof sizeStyles> & {
		className?: string;
		as?: "div" | "header" | "main" | "footer" | "section";
		id?: string;
	};

export const Container = forwardRef<never, ContainerProps>((props, ref) => {
	const { className, children, as: Component = "div", size, padding, id } = props;

	return (
		<Component ref={ref} id={id} className={twMerge(sizeStyles({ size, padding }), className)}>
			{children}
		</Component>
	);
});

Container.displayName = "Container";
